/**
 * DaysOffWidget — sidebar widget pixel-matched to the Penpot "Time Off"
 * mockup. Each row is a colored vertical bar (kind indicator) + employee
 * name + tonal status pill (Vacation / Sick Day / Personal).
 *
 * Data comes from /api/v2/dashboard/time-off-upcoming (eservbusiness.sick_leave
 * joined with eservbusiness.users for the employee name). The `reason`
 * column is free-text, so we map common spellings into the 3 visual
 * categories the design supports. Unknown reasons fall back to a neutral
 * grey pill so the entry still renders.
 *
 * Header has an "+ Add Day Off" button that navigates to the legacy
 * /absent/new page where the actual time-off form lives.
 */

import * as React from "react";
import { useHistory } from "react-router-dom";
import { StatusPill, WidgetCard } from "components/dashboard";
import { useTimeOffUpcoming, formatQueryError } from "../hooks";
import type { LayoutItem, TimeOffEntry, WidgetConfig } from "../types";

interface Props {
  widget: WidgetConfig;
  layout: LayoutItem;
}

type Kind = "vacation" | "sick" | "personal" | "other";

const KIND_BAR: Record<Kind, string> = {
  vacation: "bg-red-500",
  sick: "bg-amber-500",
  personal: "bg-purple-500",
  other: "bg-gray-400",
};

const KIND_PILL_LABEL: Record<Kind, string> = {
  vacation: "Vacation",
  sick: "Sick Day",
  personal: "Personal",
  other: "Time Off",
};

const KIND_PILL_TONE: Record<Kind, "red" | "amber" | "purple" | "gray"> = {
  vacation: "red",
  sick: "amber",
  personal: "purple",
  other: "gray",
};

/** Normalize the free-text reason into one of the 4 kinds. */
function bucketKind(reason: string | null | undefined): Kind {
  const n = (reason ?? "").toLowerCase().trim();
  if (!n) return "other";
  if (/vacation|holiday|pto/.test(n)) return "vacation";
  if (/sick|illness|medical/.test(n)) return "sick";
  if (/personal|family/.test(n)) return "personal";
  return "other";
}

export function DaysOffWidget({ widget, layout }: Props) {
  const history = useHistory();
  const { data, isLoading, error } = useTimeOffUpcoming();

  const entries: TimeOffEntry[] = data?.entries ?? [];
  const count = entries.length;

  return (
    <WidgetCard
      title="Time Off"
      w={widget.defaultSize.w}
      h={widget.defaultSize.h}
      x={layout.x}
      y={layout.y}
      loading={isLoading}
      error={error ? formatQueryError(error) : null}
      headerRight={
        <div className="flex items-center gap-2">
          {!isLoading && (
            <span className="text-[10px] text-gray-400">
              ({count} {count === 1 ? "place" : "places"})
            </span>
          )}
          <button
            type="button"
            onClick={() => history.push("/absent/new")}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-orange-500 hover:bg-orange-600 text-white text-[10px] font-semibold transition-colors"
          >
            <span className="text-[11px] leading-none">+</span>
            Add Day Off
          </button>
        </div>
      }
    >
      {entries.length === 0 ? (
        <div className="text-xs text-gray-400">No upcoming time off</div>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {entries.map((e) => (
            <li key={e.id}>
              <Row entry={e} />
            </li>
          ))}
        </ul>
      )}
    </WidgetCard>
  );
}

function Row({ entry }: { entry: TimeOffEntry }) {
  const kind = bucketKind(entry.reason);
  return (
    <div className="flex items-stretch gap-2" title={entry.reason || undefined}>
      <span
        aria-hidden
        className={`w-[3px] rounded-sm flex-shrink-0 ${KIND_BAR[kind]}`}
      />
      <div className="flex-1 min-w-0 flex items-center justify-between gap-2 py-1.5 px-2">
        <span className="text-[11px] font-medium text-gray-800 truncate">
          {entry.employeeName}
        </span>
        <StatusPill label={KIND_PILL_LABEL[kind]} tone={KIND_PILL_TONE[kind]} />
      </div>
    </div>
  );
}
